import statePromise from 'src/lib/statePromise';
import { isActionSequenceWorking, isSceneEntered } from 'src/lib/stateUtils';
import StateObserver, { user, now } from 'src/StateObserver';
import { ActionSequence } from '../../lib/ActionSequence';
import {
  getApplication,
  getSceneManager,
  useCache,
  waitForAssets,
  waitForItPromise,
  waitForMultipleAssets,
  withLoading,
} from '../../lib/utils';
import { EntityManager } from '../logic/EntityManager';
import { petSpeciesAssets } from 'src/loadingGroups';
import { PetEvolutionStage } from 'src/replicant/pet';
import { PetSpeciesID } from 'src/replicant/pet';
import * as Visit from 'src/replicant/visit';
import {
  openPopupPromise,
  PopupDialogOrigin,
} from 'src/lib/popups/popupOpenClose';
import { getFriendsPet, getPet, setFriendsPet } from '../pet';
import { PetController } from '../pet/PetController';
import i18n from 'src/lib/i18n/i18n';
import { track } from 'src/lib/analytics/utils';
import { Feature } from 'src/replicant/analytics';
import { getTutorialController } from '../tutorial/TutorialController';
import { duration } from 'src/replicant/utils/duration';
import { Position } from '../position/PositionModel';
import { FriendVisitDetails } from 'src/replicant/visit/visit.asyncGetters';
import { setLockMenu } from 'src/state/ui';
import {
  createQueryablePromiseFromPromise,
  MovieClip,
  View,
} from '@play-co/timestep-core/ui';
import { getRequiredDecorationAssets } from '../decoration/helpers';
import {
  petIdleSequence,
  showRevivalConfirmPopup,
  showRevivalPopup,
} from '../pet/pet.sequence';
import * as Fate from 'src/replicant/fate';
import {
  startTransaction,
  debugBreadcrumb,
  captureGenericError,
} from 'src/lib/sentry';
import { appendToConversationData } from '../conversation/conversation.utils';
import { FriendPetModel } from '../pet/FriendPetModel';
import * as Inventory from 'src/replicant/inventory';
import { friendRevivalCost } from 'src/replicant/visit/visit.ruleset';
import { startGemsPinchSequence } from '../shop/Helpers';
import { trackReviveFriendPet } from './visit.analytics';
import { PauseSensitiveTimeouts } from '../logic/PauseSensitiveTimeouts';

import { AssetGroup } from '@play-co/timestep-core';
import { petAnimations } from 'src/replicant/pet/PetAssets';
import { isBuruFriend } from 'src/replicant/friends/friends.getters';
import { openProfile } from '../profile/profile.sequence';
import { fetchRecentVisitors } from '../friends/friends.utils';
import type { FriendVisitOriginFeature } from './visit.analytics.schema';

type VisitSequenceArgs =
  | {
      selectedTab: 'friends' | 'visitors';
      friendId?: undefined;
    }
  | {
      selectedTab?: undefined;
      friendId: string;
    }
  | {
      selectedTab?: undefined;
      friendId?: undefined;
    };

function getSourceFeatureByTab(
  tab: VisitSequenceArgs['selectedTab'],
): FriendVisitOriginFeature | undefined {
  return tab === 'friends'
    ? 'VisitFriend'
    : tab === 'visitors'
      ? 'Visitor'
      : undefined;
}

/**
 * Wrap the visit sequence in a try catch block to handle and report errors.
 * If an error occurs, show a generic error popup to the user.
 * @param args
 */
export async function startVisitSequence(
  args: VisitSequenceArgs,
  origin: PopupDialogOrigin | undefined,
  opts?: {
    skipConfirmation?: boolean;
    skipRevivalIntro?: boolean;
    sourceFeature?: FriendVisitOriginFeature;
  },
) {
  /**
   * Ensure user will create their public profile before accessing the feature.
   * Safeguard - should not happen since visit FTUE will already ensure this
   */
  if (!user().publicProfile) {
    await openProfile(origin);
  }

  try {
    await playVisitSequence(args, origin, opts);
  } catch (error) {
    captureGenericError('VisitSequenceError', error);
    // Show error popup, so user can try again later
    await openPopupPromise('popupError', {
      title: i18n('error.general.title'),
      message: i18n('error.general.tryAgain'),
      origin,
    });
  }
}

/**
 * Resolves visit details for a friend, either directly or via a confirmation popup.
 *
 * When `skipConfirmationPopup` is true, fetches details directly with a loading indicator.
 * When false, opens `popupConfirmVisitFriend` which handles both confirmation and fetching.
 *
 * @returns The friend's visit details, or `false` if the fetch failed or the popup was dismissed.
 */
async function resolveVisitDetails(
  friendId: string,
  skipConfirmationPopup: boolean,
  sourceFeature: FriendVisitOriginFeature | undefined,
): Promise<FriendVisitDetails | false> {
  if (skipConfirmationPopup) {
    const friendVisitDetails = await withLoading(
      () => getVisitDetails(friendId).get(),
      {
        feature: Feature.VISIT._,
        subFeature: Feature.VISIT.DETAILS,
      },
    );

    if (!friendVisitDetails) {
      // If failed to fetch data, show error message and bail out.
      await openPopupPromise('popupError', {
        message: i18n(`match2.team.errors.tryAgain`),
      });
      return false;
    }

    return validateFriendForVisit(friendVisitDetails, sourceFeature);
  }

  const friendVisitDetails = await openPopupPromise('popupConfirmVisitFriend', {
    friendId,
  });

  if (!friendVisitDetails) {
    return false;
  }

  return validateFriendForVisit(friendVisitDetails, sourceFeature);
}

/**
 * Validates friend-side eligibility for a visit.
 * Checks if the friend's pet is in daycare, if the friend is online,
 * and handles the dead pet revival flow.
 *
 * @returns The (possibly updated) visit details, or `false` if the visit should not proceed.
 */
async function validateFriendForVisit(
  details: FriendVisitDetails,
  sourceFeature: FriendVisitOriginFeature | undefined,
): Promise<FriendVisitDetails | false> {
  // Bail if friend's pet is in daycare
  if (details.statusEffects.daycare) {
    track('FriendVisitStartFailure', {
      reason: 'daycare',
      targetID: details.id,
      targetIsFriend: details.isFriend,
      targetIsOnline: details.isOnline,
      targetPetIsDead: details.state === 'dead',
      originFeature: sourceFeature,
    });

    await openPopupPromise('popupModalWhite', {
      title: i18n('friendsVisit.daycare.title'),
      message: i18n('friendsVisit.daycare.message'),
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_FRIEND_IN_DAYCARE,
      },
      additionalTracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_FRIEND_IN_DAYCARE,
      },
    });
    return false;
  }

  // Bail if friend is online
  if (details.isOnline) {
    track('FriendVisitStartFailure', {
      reason: 'busy',
      targetID: details.id,
      targetIsFriend: details.isFriend,
      targetIsOnline: details.isOnline,
      targetPetIsDead: details.state === 'dead',
      originFeature: sourceFeature,
    });

    await openPopupPromise('popupModalWhite', {
      title: i18n('friendsVisit.busy.title'),
      message: i18n('friendsVisit.busy.message'),
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_FRIEND_BUSY,
      },
      additionalTracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_FRIEND_BUSY,
      },
    });
    return false;
  }

  // If friend is dead, allow player to revive them.
  if (details.state === 'dead') {
    const revive = await reviveFriend(details);
    if (!revive) {
      // Player declined revival — still return details so visit can proceed
      return details;
    }

    // After reviving the pet will be hungry.
    // Return a new object to avoid mutating the frozen Replicant response.
    return {
      ...details,
      state: 'hungry' as const,
      attributes: { ...details.attributes, ...revive.attributes },
      statusEffects: { ...details.statusEffects, ...revive.effects },
      revivedPet: true,
    };
  }

  return details;
}

async function playVisitSequence(
  { selectedTab, friendId }: VisitSequenceArgs,
  origin: PopupDialogOrigin | undefined,
  opts?: {
    skipConfirmation?: boolean;
    skipRevivalIntro?: boolean;
    sourceFeature?: FriendVisitOriginFeature;
  },
) {
  if (
    isActionSequenceWorking() &&
    ActionSequence.currentSequence === 'VisitFriend'
  ) {
    await openPopupPromise('popupModalWhite', {
      title: i18n('featureDisabled.visitingFriend.title'),
      message: i18n('featureDisabled.visitingFriend.message'),
      origin,
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_ALREADY_VISITING,
      },
      additionalTracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_ALREADY_VISITING,
      },
    });
    return;
  }

  let details: FriendVisitDetails | false;

  await ActionSequence.start(
    [
      async function showTutorial() {
        // Show tutorial.
        await getTutorialController().handleEvent('visitPopupOpened');
        return false;
      },

      async function showRevivalIntro() {
        // Skip if caller explicitly opted out of the intro.
        if (opts?.skipRevivalIntro) {
          return false;
        }

        // Skip if player has already revived a friend.
        if (user().visit.revivedFriends) {
          return false;
        }

        // Show intro if player has never revived a friend before.
        await showRevivalPopup({
          mode: 'intro',
        });

        return false;
      },

      async function selectFriendToVisit() {
        /**
         * Immediately redirect to friends tab if visitor tab is selected and there are no visitors.
         */
        if (selectedTab === 'visitors') {
          const visitors = await withLoading(
            () => fetchRecentVisitors().get(),
            {
              feature: Feature.VISIT._,
              subFeature: Feature.VISIT.RECENT_VISITORS,
            },
          );

          // If no recent visitors are found, switch to friends tab
          if (!visitors.length) {
            selectedTab = 'friends';
          }
        }

        // Continually prompt user to select friend to visit
        while (true) {
          const sourceFeature =
            opts?.sourceFeature ?? getSourceFeatureByTab(selectedTab);

          // If confirmed, carry on to next step.
          details = await showConfirmVisitFriendPopup(friendId, {
            skipConfirmation: !!opts?.skipConfirmation,
            sourceFeature,
          });

          // Skip loop if we're in non-confirmation mode and failed to get details
          if (opts?.skipConfirmation === true && details === false) {
            return true; // Bail out of the sequence if we failed to get details in non-confirmation mode
          }

          if (details) {
            return false;
          }

          // No selected tab specified, bail out.
          if (!selectedTab) {
            return true;
          }

          // Show friend selection popup
          const result = await openPopupPromise('popupVisitFriend', {
            selectedTab,
            origin,
          });

          // Update selected friend's ID, if any
          friendId = result?.friendId;

          // Preserve the last active tab on popup reopen.
          selectedTab = result?.selectedTab;

          // User pressed cancel, so break out of this sequence.
          if (!friendId) {
            return true;
          }
        }
      },

      async function beginVisitSession() {
        // No friend selected, bail out.
        if (!details) {
          return true;
        }

        debugBreadcrumb(`Begin visit session for: ${friendId}`);

        // Locks menu actions
        StateObserver.dispatch(setLockMenu(true));

        if (
          await startVisitSession({
            details,
            sourceFeature:
              opts?.sourceFeature ?? getSourceFeatureByTab(selectedTab),
          })
        ) {
          return true;
        }

        // Switch to pet's attributes.
        const friendsPet = getFriendsPet();

        if (!friendsPet) {
          throw new Error('Cannot start visit session without friends pet.');
        }

        friendsPet.isFocused = true;

        // Check if pet is dead.
        if (friendsPet.state === 'dead') {
          await friendsPet.startConversation('friend_petdead');

          // End visit
          await endVisitSession({ friendId });
          return false;
        }

        // Pet conversation
        await getPet().startConversation('friend_greetings');
        await friendsPet.startConversation('friend_welcome');

        // Update friend pet attributes in realtime
        autoRefreshFriendAttributes();

        // Release lock
        StateObserver.dispatch(setLockMenu(false));

        return false;
      },

      async function endVisitIfKickedOut() {
        debugBreadcrumb('Listen endVisitIfKickedOut start');

        // Wait until player ends the visit session or is kicked out by the friend
        await statePromise(
          ({ user }) =>
            !user.visit.visitSession || !!user.visit.visitSession.isKickedOut,
        );

        debugBreadcrumb('Execute endVisitIfKickedOut');

        // Locks menu actions
        StateObserver.dispatch(setLockMenu(true));

        // Stop updating friends attributes
        stopAutoRefreshFriendAttributes();

        const { user } = StateObserver.getState();

        if (!Visit.getters.isVisiting(user)) {
          // There's no friends pet anymore, so we can just return.
          if (!getFriendsPet()) {
            // Release lock
            StateObserver.dispatch(setLockMenu(false));
            return false;
          }

          // Still in the visit scene, so the session ended unexpectedly. For
          // example if the the player leaves the app in the background for too long.
          if (isSceneEntered('friendsLivingRoom')) {
            // Force the pet to go back to the living room.
            debugBreadcrumb('endVisitIfKickedOut end session 1');
            await endVisitSession({
              friendId: getFriendsPet()?.id,
              sendMessage: false,
            });
          }

          // Release lock
          StateObserver.dispatch(setLockMenu(false));

          // Player ended the visit session so return normally
          return false;
        }

        // Friend kicked us out
        // Wait until we're idling in the living room
        await statePromise(
          ({ care }) =>
            isSceneEntered('friendsLivingRoom') &&
            !care.currentCareAction &&
            !getPet().conversation.inProgress() &&
            !getFriendsPet()?.conversation.inProgress(),
        );

        // Play conversation
        await getPet().startConversation('friend_kickedout');

        // End visit
        const { visitSession } = user.visit;
        debugBreadcrumb('endVisitIfKickedOut end session 2');
        await endVisitSession({ friendId: visitSession?.friendId });

        // Release lock
        StateObserver.dispatch(setLockMenu(false));

        return true;
      },
    ],
    'VisitFriend',
  );
}

export async function startVisitSession({
  details,
  playerTargetPosition,
  friendTargetPosition,
  overrides,
  sourceFeature,
}: {
  details: FriendVisitDetails;
  playerTargetPosition?: Position;
  friendTargetPosition?: Position;
  overrides?: Partial<{
    evolution: { species: PetSpeciesID; stage: PetEvolutionStage };
    age: number;
    petName: string;
  }>;
  sourceFeature?: FriendVisitOriginFeature;
}) {
  // An combined transaction for visit session start
  const visitTransaction = startTransaction({ name: 'visit-start-sequence' });

  // If there's an active session try to end it.
  if (user().visit.visitSession) {
    await StateObserver.invoke.endVisit(true);
  }

  try {
    await StateObserver.invoke.beginVisit({
      petDetails: details,
      sourceFeature,
    });
  } catch (error) {
    track('FriendVisitStartFailure', {
      reason: 'error',
      targetID: details.id,
      targetIsFriend: details.isFriend,
      targetIsOnline: details.isOnline,
      targetPetIsDead: details.state === 'dead',
      originFeature: sourceFeature,
      errorMessage: error instanceof Error ? error.message : String(error),
    });

    throw error;
  }

  // Apply overrides — create a new object to avoid mutating the frozen Replicant response.
  if (overrides) {
    details = {
      ...details,
      age: overrides.age ?? details.age,
      attributes: {
        ...details.attributes,
        name: overrides.petName ?? details.attributes.name,
      },
      currentEvolution: { ...details.currentEvolution, ...overrides.evolution },
    };
  }

  // Load friend's species assets.
  await waitForMultipleAssets(
    [
      petSpeciesAssets[details.currentEvolution?.species],
      getRequiredDecorationAssets('living', details.decorations),
      getRequiredDecorationAssets('toilet', details.decorations),
    ],
    {
      feature: Feature.VISIT._,
      subFeature: Feature.VISIT.PET_AND_DECO_ASSETS,
    },
  );

  appendToConversationData(details.currentEvolution?.species);

  // Past this point there's only animations
  visitTransaction.finish();

  const friendPet = EntityManager.createOrGetFriendsPet(details.id);

  // Pets are created with user's pet species by default, so we update the species here,
  // to prevent the missing animation error in the first play.
  if (details.currentEvolution?.species) {
    friendPet.setSpecies(details.currentEvolution?.species);
  }

  const friendPetController = new PetController(getSceneManager(), friendPet);
  setFriendsPet(friendPetController);

  const { level, xp } = details;

  friendPetController.init(
    new FriendPetModel(
      details.id,
      details.currentEvolution?.stage,
      details.features,
      details.decorations,
    ),
    {
      pet: {
        species: details.currentEvolution?.species,
        statusEffects: details.statusEffects,
        attributes: {
          ...details.attributes,
          age: details.age,
        },
      },
      level: {
        current: level,
        xp,
      },
      accessories: details.accessories,
    },
  );

  const petController = getPet();

  // Block the screen to avoid any interaction between scene transition finishing and pets starting conversation
  const blocker = new View({
    superview: getApplication().getRootView(),
    infinite: true,
    canHandleEvents: true,
    zIndex: 10000, // Under popups.
  });

  await Promise.all([
    getPet().moveTo(
      playerTargetPosition ?? {
        scene: 'friendsLivingRoom',
        location: 'lm',
      },
    ),
    friendPetController.moveTo(
      friendTargetPosition ?? {
        scene: 'friendsLivingRoom',
        location: 'rm',
      },
      0,
    ),
  ]);
  await waitForItPromise();

  // Remove the blocker
  blocker.removeFromSuperview();

  petController.appearance.setAction(undefined);

  return false;
}

export async function endVisitSession({
  friendId,
  transitionToLivingRoom = true,
  sendMessage = true,
}: {
  friendId: string | undefined;
  transitionToLivingRoom?: boolean;
  sendMessage?: boolean;
}) {
  debugBreadcrumb(`End session for: ${friendId}`);

  if (!friendId) {
    return;
  }

  // Make sure we start moving before ending the session.
  let movementPromise = Promise.resolve();
  if (transitionToLivingRoom) {
    const pet = getPet();
    const { appearance } = pet;

    // Transition back to living room
    movementPromise = pet
      .moveTo({
        scene: 'livingRoom',
        location: 'c',
      })
      .then(() => {
        appearance.unlock();
        appearance.setAction(undefined);
        pet.position.setWandering(true);
      });
  }

  // End session.
  const endSessionPromise = createQueryablePromiseFromPromise(
    user().visit.visitSession
      ? StateObserver.invoke.endVisit(sendMessage)
      : Promise.resolve(),
  );

  // Lock menu until visit session is cleaned up
  StateObserver.dispatch(setLockMenu(true));
  try {
    // Await movement.
    await movementPromise;

    // If session was not ended yet, show loading spinner.
    if (!endSessionPromise.isReady()) {
      await withLoading(() => endSessionPromise, {
        feature: Feature.VISIT._,
        subFeature: Feature.VISIT.END_VISIT,
      });
    }
  } finally {
    StateObserver.dispatch(setLockMenu(false));
  }

  // Dispose of friend's pet.
  debugBreadcrumb(`Pet destroy for: ${friendId}`);
  getFriendsPet()?.destroy();
  setFriendsPet(null);

  // Start pet idle sequence.
  await petIdleSequence();
}

async function showConfirmVisitFriendPopup(
  friendId: string | undefined,
  opts: {
    skipConfirmation: boolean;
    sourceFeature: FriendVisitOriginFeature | undefined;
  },
): Promise<FriendVisitDetails | false> {
  // No friend selected, bail out.
  if (!friendId) return false;

  // Player pet is dead.
  if (Fate.getters.getVisiblePetState(user(), StateObserver.now()) === 'dead') {
    await openPopupPromise('popupModalWhite', {
      title: i18n('featureDisabled.dead.title'),
      message: i18n('featureDisabled.dead.message'),
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_PLAYER_DEAD,
      },
      additionalTracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_PLAYER_DEAD,
      },
    });
    return false;
  }

  // Show visit confirmation popup or just fetch visit details directly, depending on skipConfirmation flag
  const friendVisitDetails = await resolveVisitDetails(
    friendId,
    opts.skipConfirmation,
    opts.sourceFeature,
  );

  if (!friendVisitDetails) {
    return false;
  }

  // Bail if pet is in daycare
  if (
    Fate.getters.hasPermanentStatusEffect(
      user(),
      'daycare',
      StateObserver.now(),
    )
  ) {
    await openPopupPromise('popupModalWhite', {
      title: i18n('featureDisabled.visitFriend-daycare.title'),
      message: i18n('featureDisabled.visitFriend-daycare.message'),
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_PLAYER_IN_DAYCARE,
      },
      additionalTracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_PLAYER_IN_DAYCARE,
      },
    });
    return false;
  }

  return friendVisitDetails;
}

/**
 * Refreshes the pet's attributes.
 */
export async function refreshPetAttributes() {
  const friendsPet = getFriendsPet();

  // Check if friend's pet exists. Might have been deleted.
  if (!friendsPet) {
    return;
  }

  // Check if a care action is in progress.
  if (StateObserver.getState().care.currentCareAction) {
    return;
  }

  const attributes =
    await StateObserver.replicant.asyncGetters.getFriendAttributeValues(
      friendsPet.id,
    );

  if (!attributes) return;

  friendsPet.appearance.setAttributes(attributes);
}

/**
 * Get the visit friends list, reusing a request made within the last minute.
 *
 * Players often reopen the list within seconds: backing out of the visit confirmation reopens
 * it, as does tapping the selected tab. The list shows who is offline, so it is kept short;
 * `canVisit` still blocks a visit to a friend who has come online since.
 */
export function loadVisitFriendsList() {
  return useCache(
    () => StateObserver.replicant.asyncGetters.getVisitFriendsList(),
    'visitFriendsList',
    duration({ seconds: 60 }),
  ).get();
}

/**
 * Revive friend.
 */
export async function reviveFriend(details: FriendVisitDetails) {
  const free = !user().visit.revivedFriends;

  const assetGroup = new AssetGroup([
    ...AssetGroup.constructURLs(['assets/ui/visit/revive']),
    {
      url: petAnimations[details.currentEvolution.species].url,
      loadMethod: MovieClip.animationLoader,
    },
  ]);

  await waitForAssets(assetGroup, {
    feature: Feature.VISIT._,
    subFeature: Feature.VISIT.REVIVE_CONFIRM,
  });

  let revive = await showRevivalConfirmPopup({
    species: details.currentEvolution.species,
    free,
  });

  if (!revive) {
    revive = await showRevivalConfirmPopup({
      species: details.currentEvolution.species,
      free,
      secondTime: true,
    });
    if (!revive) {
      return false;
    }
  }

  if (!free && Inventory.getters.getGems(user()) < friendRevivalCost) {
    await startGemsPinchSequence({
      feature: Feature.VISIT.REVIVE,
      crossplayTracking: {
        feature: Feature.VISIT._,
        subFeature: Feature.VISIT.REVIVE,
      },
    });

    // Check if player has enough gems now.
    if (Inventory.getters.getGems(user()) < friendRevivalCost) {
      return false;
    }
  }

  const fateDiff = await StateObserver.invoke.reviveFriend(details.id);

  // analytics
  trackReviveFriendPet({
    friendID: details.id,
    petAge: details.age,
    daysElapsedLastEntry:
      Math.round(((now() - details.lastLogin) / duration({ days: 1 })) * 1000) /
      1000,
    daysSincePetDie:
      Math.round(
        ((now() - details.deadStateTimestamp!) / duration({ days: 1 })) * 1000,
      ) / 1000,
  });

  await showRevivalPopup({
    mode: 'hungry',
    species: details.currentEvolution.species,
  });

  // Send back to life OA if U2U can be sent.
  if (isBuruFriend(user(), details.id)) {
    await StateObserver.invoke.sendBackToLifeOA({ friendId: details.id });
  }
  // Send revive OA message otherwise.
  else {
    await StateObserver.invoke.sendReviveOA({ friendId: details.id });
  }

  return fateDiff;
}

export function getVisitDetails(friendId: string) {
  return useCache(
    () => StateObserver.replicant.asyncGetters.getPetVisitDetails(friendId),
    `visitDetails${friendId}`,
    duration({ seconds: 10 }),
  );
}

let timer: ReturnType<typeof PauseSensitiveTimeouts.create> | undefined;

/**
 * Refreshes the friend's pet attributes every 10 seconds.
 */
function autoRefreshFriendAttributes() {
  // Clear existing timeout, just in case
  if (timer) PauseSensitiveTimeouts.clear(timer);
  // Create the timeout that will call refreshPetAttributes every 10 seconds
  timer = PauseSensitiveTimeouts.create(
    () => {
      refreshPetAttributes();
      autoRefreshFriendAttributes();
    },
    duration({ seconds: 10 }),
  );
}

/**
 * Stops the auto refresh of the friend's pet attributes.
 */
function stopAutoRefreshFriendAttributes() {
  if (!timer) return;
  PauseSensitiveTimeouts.clear(timer);
  timer = undefined;
}
